import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import PartnersApi from '@/api/partners'
import { FieldType } from '@/constants/fieldType'
import { HttpStatus } from '@/constants/httpStatus'
import { partnerEntity } from '@/models/models'
import { getFieldError } from '@/utils/textValidationFunctions'
import { cloneDeep, merge } from 'lodash'

@Module({ namespaced: true })
export default class PartnerFormModule extends VuexModule {
    public isLoading: boolean = false
    public partner: PartnerEntity = partnerEntity
    public categories: PartnerCategoryEntity[] = []

    @Mutation
    public setLoading(loading: boolean) {
        this.isLoading = loading
    }

    @Mutation
    public setPartner(partner: PartnerEntity) {
        this.partner = partner
    }

    @Mutation
    public setCategories(categories: PartnerCategoryEntity[]) {
        this.categories = categories
    }

    @Action
    public async getPartner(id: number) {
        this.context.commit('setLoading', true)

        const response = await PartnersApi.getPartner(id)

        if (response.status === HttpStatus.CANCELLED) {
            return null
        }

        if (response.status === HttpStatus.SUCCESS) {
            this.context.commit('setPartner', response.data!.partner)
            this.context.commit('setCategories', response.data!.categories)
        } else {
            alert(response.message)

            this.context.commit('setLoading', false)
            return null
        }

        this.context.commit('setLoading', false)
        return (response.data && response.data.partner) ? cloneDeep(response.data.partner) : null
    }

    @Action
    public async savePartner(entity: PartnerEntity): Promise<PartnerEntity | null> {
        const partner = cloneDeep(entity)

        partner.errors = await this.context.dispatch('validatePartner', partner)

        // Erros de preenchimento no frontend
        if (partner.errors != null && Object.keys(partner.errors).length > 0) {
            return Promise.resolve(partner)
        }

        this.context.commit('setLoading', true)

        const response = await (partner.id > 0 ?
            PartnersApi.updatePartner(partner) :
            PartnersApi.createPartner(partner)
        )

        // Erro de requisição
        if (response.status === HttpStatus.ERROR || response.status === HttpStatus.CANCELLED) {
            return Promise.resolve(null)

        // Erros reportados no backend
        } else if (response.status === HttpStatus.FAIL) {
            partner.errors = response.errors

        // Sucesso
        } else {
            partner.id = response.data!.partner!.id
        }

        this.context.commit('setLoading', false)

        return Promise.resolve(partner)
    }

    @Action
    private async validatePartner(entity: PartnerEntity): Promise<any> {
        let errors = {}

        const fieldsToVerify = [
            { key: 'name', value: entity.name, type: FieldType.TEXT },
            { key: 'category', value: entity.category!.id, type: FieldType.SELECT },
            { key: 'company', value: entity.company, type: FieldType.TEXT },
            { key: 'cnpj', value: entity.cnpj, type: FieldType.CNPJ },
            { key: 'email', value: entity.email, type: FieldType.EMAIL },
            { key: 'addressStreet', value: entity.address!.street, type: FieldType.TEXT },
            { key: 'addressNumber', value: entity.address!.number, type: FieldType.NUMBER },
            { key: 'addressNeighborhood', value: entity.address!.neighborhood, type: FieldType.TEXT },
            { key: 'addressCity', value: entity.address!.city, type: FieldType.TEXT },
            { key: 'addressState', value: entity.address!.state, type: FieldType.TEXT },
            { key: 'addressZipcode', value: entity.address!.zipcode, type: FieldType.TEXT },
            { key: 'contactsTecnicalName', value: entity.contacts!.tecnical!.name, type: FieldType.TEXT },
            { key: 'contactsTecnicalEmail', value: entity.contacts!.tecnical!.email, type: FieldType.EMAIL },
        ]

        fieldsToVerify.forEach((field) => {
            const fieldError = getFieldError(field.value, field.type)

            if (fieldError !== '') {
                errors = merge(errors, { [field.key]: fieldError })
            }
        })

        return Promise.resolve(errors)
    }
}
